import { notEmpty } from "@product/scmp-sdk";
import first from "lodash/first";
import last from "lodash/last";
import type { FunctionComponent } from "react";
import { Fragment } from "react";
import { graphql, useFragment } from "react-relay";

import { GenericPostiesLeading } from "~/components/article/article-leading/generic-posties-article-leading";
import type { DefaultComponentsProvider } from "~/components/article/article-render";
import { useArticleBaseAdvertisingInfo } from "~/components/article/article-render/hooks";
import {
  GenericPostiesArticleTableThead,
  GenericPostiesArticleTableTr,
} from "~/components/article/article-render/posties/generic-posties-article/table/styles";
import { GenericPostiesArticleVocabBox } from "~/components/article/article-vocab-box/generic-posties-article-vocab-box";
import { ContentBody } from "~/components/content/content-body";
import { PostiesPaywallContainer } from "~/components/posties/paywall-container";
import { formatInTimeZone } from "~/lib/utils";
import type { genericPostiesArticleContent$key } from "~/queries/__generated__/genericPostiesArticleContent.graphql";

import { GenericPostiesArticleBlockquoteQuote } from "./blockquote-quote";
import {
  Authors,
  AuthorsAndPublishedDate,
  Content,
  DesktopLeft,
  GenericPostiesArticleContainer,
  Heading1,
  Heading2,
  Heading3,
  Heading4,
  Heading5,
  Heading6,
  Headline,
  MobileBottom,
  MobileTop,
  PublishedDate,
  Right,
  Section,
  StyledTopBackgroundImage,
  TopBackgroundContainer,
  TopBlock,
} from "./styles";
import { GenericPostiesArticleTable } from "./table";
import TopBackground from "./top-background.png";

type Props = {
  provider: DefaultComponentsProvider;
  reference: genericPostiesArticleContent$key;
};

export const GenericPostiesArticle: FunctionComponent<Props> = ({
  provider,
  reference: reference_,
}) => {
  const article = useFragment(
    graphql`
      fragment genericPostiesArticleContent on Article
      @argumentDefinitions(customContents: { type: "[CustomContentInput]", defaultValue: [] }) {
        sections {
          value {
            name
          }
        }
        authors {
          name
        }
        publishedDate
        additionalInfoBoxes {
          ...articleAdditionalInfoBoxArticle
        }
        ...genericPostiesArticleVocabBoxArticle
        ...blockquoteQuoteGenericPostiesArticleContent
        ...articleBaseAdvertisingInfoArticle
        ...contentBodyContent @arguments(customContents: $customContents)
      }
    `,
    reference_,
  );

  const additionalInfoBoxes = article?.additionalInfoBoxes
    ?.filter(notEmpty)
    .map?.((item, index) => (
      <Fragment key={index}>{provider.articleAdditionalInfoBox({ reference: item })}</Fragment>
    ));

  const [mobileTopAdditionInfoBox, ...mobileBottomAdditionInfoBoxes] = additionalInfoBoxes ?? [];

  const advertising = useArticleBaseAdvertisingInfo(article);

  return (
    <GenericPostiesArticleContainer>
      <TopBackgroundContainer>
        <StyledTopBackgroundImage alt={TopBackground.alt} src={TopBackground.src} />
      </TopBackgroundContainer>
      <TopBlock>
        <Section>{last(first(article?.sections)?.value)?.name}</Section>
        <Headline>{provider.headline()}</Headline>
        <AuthorsAndPublishedDate>
          <Authors>{article?.authors?.map(author => author?.name).join(", ")}</Authors>
          <PublishedDate>
            {article?.publishedDate &&
              `${formatInTimeZone(article.publishedDate ?? "", "d MMM, yyyy")}`}
          </PublishedDate>
        </AuthorsAndPublishedDate>
      </TopBlock>
      <GenericPostiesLeading provider={provider} />
      <DesktopLeft>{additionalInfoBoxes}</DesktopLeft>
      <MobileTop>{mobileTopAdditionInfoBox}</MobileTop>
      <Content>
        <ContentBody
          advertising={advertising}
          extraComponentMap={{
            h1: Heading1,
            h2: Heading2,
            h3: Heading3,
            h4: Heading4,
            h5: Heading5,
            h6: Heading6,
            "inline-widget": null,
            table: GenericPostiesArticleTable,
            thead: GenericPostiesArticleTableThead,
            tr: GenericPostiesArticleTableTr,
          }}
          extraRenderFunctionMap={{
            "blockquote-quote": (
              props,
              itemIndex,
              index,
              _children,
              schemaNode,
              parentSchemaNode,
            ) => (
              <GenericPostiesArticleBlockquoteQuote
                {...props}
                index={index}
                key={itemIndex}
                parentSchemaNode={parentSchemaNode}
                reference={article}
                schemaNode={schemaNode}
              />
            ),
          }}
          reference={article}
        />
      </Content>
      <MobileBottom>{mobileBottomAdditionInfoBoxes}</MobileBottom>
      <PostiesPaywallContainer />
      <Right />
      <GenericPostiesArticleVocabBox reference={article} />
    </GenericPostiesArticleContainer>
  );
};

GenericPostiesArticle.displayName = "GenericPostiesArticle";
